import { memo, useMemo } from "react";
import { useTenantBalances } from "shared/components/tenant";
import { Button } from "components/ui/button";
import { cn } from "@/lib/utils";
import {
  ListBulletsIcon,
  KanbanIcon,
  BuildingsIcon,
} from "@phosphor-icons/react";
import { toast } from "react-toastify";
import { useSession } from "@clerk/clerk-react";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { FilterBar } from "shared/components/filterBar";
import {
  UniversalTable,
  TABLE_DESIGN,
} from "shared/components/UniversalTable";
import { TablePagination } from "components/TablePagination";
import { createHelpdeskCells } from "../shared/helpdeskCellFactory";
import { HELPDESK_TABLE_CONFIG } from "../configs/helpdeskTableConfig";
import { ExcelExportModal } from "shared/components/ExcelExportModal";
import { HELPDESK_EXPORT_COLUMNS } from "../configs/helpdeskExportColumns";
import { UniversalPickerModal } from "shared/components/UniversalPickerModal";
import { QuickAddTicketModal } from "../components/quickAddTicketModal";
import { CreateInsuranceIssueModal } from "../components/createInsuranceIssueModal";
import { MergeTicketsModal } from "../components/mergeTicketsModal";
import { DeleteTicketModal } from "../components/deleteTicketModal";
import { SupplierSuggestionModal } from "../components/supplierSuggestionModal";
import { useHelpdeskShared } from "../shared/hooks/useHelpdeskShared";
import { useNavigateWithFilters } from "../shared";
import { TicketInternalStatus, TicketPriority } from "shared/types/helpdesk";
import { useHelpdeskListViewMode } from "../shared/hooks/useHelpdeskListViewMode";
import { useHelpdeskTicketGroups } from "../shared/hooks/useHelpdeskTicketGroups";
import { HelpdeskBuildingGroupedTable } from "../components/HelpdeskBuildingGroupedTable";

/**
 * HelpdeskListPage - List view for Helpdesk tickets
 *
 * This component renders the helpdesk tickets in a table/list format.
 * It uses the useHelpdeskShared hook to share state with HelpdeskKanbanPage.
 *
 * Features:
 * - UniversalTable with lazy loading
 * - FilterBar with smart insights
 * - Bulk selection and actions
 * - View mode toggle (List/Kanban)
 * - Statistics badges (Open/Completion/Total counts)
 * - All modals (QuickAdd, Merge, Delete, Insurance, Excel, Bulk Phase, Supplier Suggestion)
 */
const HelpdeskListPageComponent = () => {
  const { session } = useSession();
  const navigate = useNavigateWithFilters();

  // Use shared hook with 'list' view mode
  const state = useHelpdeskShared("list");

  // Weergave: vlakke lijst of gegroepeerd per gebouw. Zelfde knop als bij de
  // renovatietickets (BUG-635), daar per project, hier per gebouw.
  const { viewMode: listViewMode, setViewMode: setListViewMode } =
    useHelpdeskListViewMode();

  const {
    // Data
    filters,
    filteredTickets,
    totalCount,
    openCount,
    completionCount,
    isLoadingTotalCount,

    // List pagination
    listPage,
    setListPage,
    listPageSize,
    isListLoading,
    listTotal,

    // Filter states
    searchText,
    setSearchText,
    filterBarSections,
    filterBarChips,
    isRestoringFromURL,
    handleFiltersFromURL,

    // Actions
    refreshTickets,
    handleUpdateTicket,
    handleCompleteTicket,
    handleMoveToCompletion,
    handleRevertToOpen,
    handleSmartInsights,
    handleMerge,
    handleDelete,
    handleCreateInsurance,

    // Bulk actions
    selectedOpenTickets,
    setSelectedOpenTickets,
    bulkActions,
    handleExcelExportWithColumns,

    // Modal states
    quickAddModalOpen,
    setQuickAddModalOpen,
    mergeModalOpen,
    setMergeModalOpen,
    mergeSource,
    setMergeSource,
    mergeTarget,
    setMergeTarget,
    mergeSearchSource,
    setMergeSearchSource,
    mergeSearchTarget,
    setMergeSearchTarget,
    deleteModal,
    setDeleteModal,
    insuranceModalOpen,
    setInsuranceModalOpen,
    selectedTicketForInsurance,
    supplierSuggestionOpen,
    setSupplierSuggestionOpen,
    suggestedSuppliers,
    suggestionForLabel,
    suggestionForTicketIds,
    isUpdatingSupplier,
    applySuggestedSupplier,
    excelExportModalOpen,
    setExcelExportModalOpen,
    bulkPhasePickerOpen,
    setBulkPhasePickerOpen,
    handleBulkPhaseConfirm,

    // Column actions
    columnActions,
    handleModalRequest,

    // Users
    users,
  } = state;

  // Handle merge confirmation
  const handleMergeConfirm = async () => {
    if (!mergeSource || !mergeTarget) {
      toast.error("Selecteer zowel een bron als doel ticket");
      return;
    }

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/tickets/merge`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            sourceId: mergeSource.id,
            targetId: mergeTarget.id,
          }),
        },
        session,
      );

      if (response.ok) {
        toast.success(
          `Tickets samengevoegd: H${mergeSource.id} → H${mergeTarget.id}`,
        );
        setMergeModalOpen(false);
        setMergeSource(null);
        setMergeTarget(null);
        await refreshTickets();
      } else {
        const error = await response.json();
        toast.error(`Fout bij samenvoegen: ${error.message}`);
      }
    } catch (error) {
      console.error("Error merging tickets:", error);
      toast.error("Fout bij samenvoegen van tickets");
    }
  };

  // Handle delete confirmation
  const handleDeleteConfirm = async () => {
    if (!deleteModal.ticket) return;

    try {
      const response = await fetchWithAuth(
        `${API_URL}/helpdesk/ticket/${deleteModal.ticket.id}`,
        { method: "DELETE" },
        session,
      );

      if (response.ok) {
        toast.success(`Ticket H${deleteModal.ticket.id} verwijderd`);
        setDeleteModal({ open: false, ticket: null });
        await refreshTickets();
      } else {
        throw new Error("Failed to delete ticket");
      }
    } catch (error) {
      console.error("Error deleting ticket:", error);
      toast.error("Fout bij verwijderen van ticket");
    }
  };

  // Render helper for table row - UniversalTable passes (row, context)
  // Openstand van de melders op deze pagina — één call voor alle rijen samen, niet
  // één per rij. Zonder openstand toont het badge niets.
  const { balances: tenantBalances } = useTenantBalances(
    useMemo(
      () => (filteredTickets ?? []).map((ticket: any) => ticket?.reporter?.id),
      [filteredTickets],
    ),
  );

  // De gegroepeerde lijst haalt haar eigen gegevens op, met dezelfde filters.
  // `enabled` houdt die call stil zolang je in de vlakke weergave werkt.
  const ticketGroups = useHelpdeskTicketGroups({
    filters,
    enabled: listViewMode === "building",
  });

  const renderRow = (ticket: any, context: any) => {
    // Helper to render priority stars with click handlers
    const renderPriority = (priority: any) => {
      const totalStars = 3;
      // Map Dutch priority values to star count
      let filledStars =
        priority === TicketPriority.HIGH
          ? 3
          : priority === TicketPriority.MEDIUM
            ? 2
            : priority === TicketPriority.LOW
              ? 1
              : 0;

      const handleStarClick = async (
        e: React.MouseEvent,
        starIndex: number,
      ) => {
        e.stopPropagation();
        const newStarCount = starIndex + 1;
        let newPriority: TicketPriority;

        if (newStarCount === 3) newPriority = TicketPriority.HIGH;
        else if (newStarCount === 2) newPriority = TicketPriority.MEDIUM;
        else newPriority = TicketPriority.LOW;

        // Optimistically update local state
        handleUpdateTicket(ticket.id!, { priority: newPriority });

        // Persist to backend
        try {
          const response = await fetchWithAuth(
            `${API_URL}/helpdesk/ticket/${ticket.id}`,
            {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ priority: newPriority }),
            },
            session,
          );

          if (!response.ok) {
            throw new Error("Failed to update priority");
          }
        } catch (error) {
          console.error("Error updating priority:", error);
          toast.error("Fout bij updaten van prioriteit");
          // Revert optimistic update on error
          handleUpdateTicket(ticket.id!, { priority: priority });
        }
      };

      return (
        <div className="flex items-center gap-1">
          {[...Array(totalStars)].map((_, index) => {
            const isActive = index < filledStars;
            return (
              <span
                key={index}
                className={`${TABLE_DESIGN.cellContent.priority.star} ${isActive ? TABLE_DESIGN.cellContent.priority.active : TABLE_DESIGN.cellContent.priority.inactive} ${TABLE_DESIGN.cellContent.priority.interactive}`}
                onClick={(e) => handleStarClick(e, index)}
              >
                {isActive ? "★" : "☆"}
              </span>
            );
          })}
        </div>
      );
    };

    return createHelpdeskCells({
      ticket,
      context,
      handlers: {
        onComplete: (e: React.MouseEvent) => {
          e.stopPropagation();
          handleCompleteTicket(ticket.id!);
        },
        onMoveToCompletion: async (e: React.MouseEvent) => {
          e.stopPropagation();
          await handleMoveToCompletion(ticket.id!);
        },
        onRevertToOpen: async (e: React.MouseEvent) => {
          e.stopPropagation();
          await handleRevertToOpen(ticket.id!);
        },
        onSmartInsights: () => handleSmartInsights(ticket),
        onMerge: () => handleMerge(ticket),
        onDelete: () => handleDelete(ticket),
        onCreateInsurance: (e: React.MouseEvent) => {
          e.stopPropagation();
          handleCreateInsurance(ticket);
        },
        onTitleClick: (e: React.MouseEvent) => {
          e.stopPropagation();
          navigate(`/helpdesk/${ticket.id}`, {
            additionalParams: { from: "list", viewMode: "list" },
          });
        },
      },
      renderPriority,
      users,
      tenantBalances,
    });
  };

  return (
    <>
      <div className="bg-brick-secondary-fine flex flex-col gap-0">
        {/* Header with View Mode Toggle */}
        <header className="px-6 pt-6 pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Helpdesk Tickets
                </h1>
              </div>
            </div>

            {/* Pagination, View Mode Toggle and Add Button */}
            <div className="flex items-center gap-3">
              {/* Paginatie hoort bij de vlakke lijst; de weergave per gebouw
                  pagineert niet (je moet een gebouw in één keer kunnen
                  open- en dichtklikken). */}
              {listViewMode === "recent" && (
                <TablePagination
                  page={listPage}
                  pageSize={listPageSize}
                  totalCount={listTotal}
                  onPageChange={setListPage}
                  isLoading={isListLoading}
                />
              )}
              <div className="border-input bg-background flex items-center gap-1 rounded-lg border p-1 shadow-sm">
                <button
                  onClick={() => {
                    setListViewMode("recent");
                    navigate("/helpdesk/list");
                  }}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    listViewMode === "recent"
                      ? "bg-moss-primary text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                  aria-label="Lijstweergave"
                  title="Lijstweergave"
                >
                  <ListBulletsIcon size={18} weight="fill" />
                  <span className="hidden sm:inline">Lijst</span>
                </button>

                <button
                  onClick={() => setListViewMode("building")}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    listViewMode === "building"
                      ? "bg-moss-primary text-white shadow-sm"
                      : "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                  aria-label="Weergave per gebouw"
                  title="Gegroepeerd per gebouw en unit"
                >
                  <BuildingsIcon size={18} weight="fill" />
                  <span className="hidden sm:inline">Per gebouw</span>
                </button>

                <button
                  onClick={() => navigate("/helpdesk/kanban")}
                  className={cn(
                    "flex items-center gap-2 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    "text-muted-foreground hover:text-foreground hover:bg-muted",
                  )}
                  aria-label="Kanbanweergave"
                  title="Kanbanweergave"
                >
                  <KanbanIcon size={18} weight="regular" />
                  <span className="hidden sm:inline">Kanban</span>
                </button>
              </div>
              <Button onClick={() => setQuickAddModalOpen(true)} size="sm">
                <span className="h-5 w-5 text-lg font-bold">+</span>
                Ticket toevoegen
              </Button>
            </div>
          </div>
        </header>

        {/* Ticket Statistics Display */}
        <div className="px-6 pt-4 pb-2">
          {isLoadingTotalCount ? (
            <div className="text-sm text-gray-600">Statistieken laden...</div>
          ) : (
            <div className="flex items-center gap-6">
              {/* Open Tickets */}
              <div className="flex flex-col">
                <span className="text-neutral-primary text-2xl font-bold">
                  {openCount ?? "..."}
                </span>
                <span className="text-xs text-gray-500 uppercase">
                  Open tickets
                </span>
              </div>

              {/* Separator */}
              <div className="h-10 w-px bg-gray-300"></div>

              {/* Completion Phase Tickets */}
              <div className="flex flex-col">
                <span className="text-moss-primary text-2xl font-bold">
                  {completionCount ?? "..."}
                </span>
                <span className="text-xs text-gray-500 uppercase">
                  In afronding
                </span>
              </div>

              {/* Separator */}
              <div className="h-10 w-px bg-gray-300"></div>

              {/* Total Non-Closed */}
              <div className="flex flex-col">
                <span className="text-moss-primary text-2xl font-bold">
                  {totalCount ?? "..."}
                </span>
                <span className="text-xs text-gray-500 uppercase">
                  Totaal (excl. gesloten)
                </span>
              </div>

            </div>
          )}
        </div>

        {listViewMode === "building" ? (
          <HelpdeskBuildingGroupedTable
            rows={ticketGroups.rows}
            groupsByKey={ticketGroups.groupsByKey}
            unitsByKey={ticketGroups.unitsByKey}
            isLoading={ticketGroups.isLoading}
            selectedRows={selectedOpenTickets}
            onSelectionChange={setSelectedOpenTickets}
            renderRow={renderRow}
            bulkActions={bulkActions}
            columnActions={columnActions}
            onModalRequest={handleModalRequest}
            onUpdate={handleUpdateTicket}
            onComplete={handleCompleteTicket}
            onMoveToCompletion={handleMoveToCompletion}
            onRevertToOpen={handleRevertToOpen}
            truncated={ticketGroups.truncated}
            loaded={ticketGroups.loaded}
            total={ticketGroups.total}
          />
        ) : (
          <UniversalTable
            columnsConfig={HELPDESK_TABLE_CONFIG.columnsConfig}
            pickerConfig={HELPDESK_TABLE_CONFIG.pickerConfig}
            sortConfig={HELPDESK_TABLE_CONFIG.sortConfig}
            rows={filteredTickets}
            groupBy="none"
            title=""
            isLoading={isListLoading}
            renderRow={renderRow}
            selectedRows={selectedOpenTickets}
            onSelectionChange={setSelectedOpenTickets}
            searchConfig={{
              bulkActions: bulkActions,
            }}
            columnActions={columnActions}
            onModalRequest={handleModalRequest}
            onUpdate={handleUpdateTicket}
            onComplete={handleCompleteTicket}
            onMoveToCompletion={handleMoveToCompletion}
            onRevertToOpen={handleRevertToOpen}
          />
        )}

      </div>

      {/* FilterBar at parent level - applies to all views */}
      <FilterBar
        searchValue={searchText}
        onSearchChange={(value) => {
          if (!isRestoringFromURL) {
            setSearchText(value);
          }
        }}
        searchPlaceholder="Zoek tickets op ID, titel, melder..."
        filterChips={filterBarChips}
        filterSections={filterBarSections}
        syncWithURL={true}
        urlParamPrefix=""
        onFiltersFromURL={handleFiltersFromURL}
        showWhenEmpty={true}
        showSpacer={false}
      />

      {/* Modals */}
      <QuickAddTicketModal
        isOpen={quickAddModalOpen}
        onClose={() => setQuickAddModalOpen(false)}
        onTicketAdded={refreshTickets}
      />

      {mergeModalOpen && (
        <MergeTicketsModal
          isOpen={mergeModalOpen}
          onClose={() => {
            setMergeModalOpen(false);
            setMergeSource(null);
            setMergeTarget(null);
          }}
          onConfirm={handleMergeConfirm}
          sourceTicket={mergeSource}
          targetTicket={mergeTarget}
          onSourceChange={setMergeSource}
          onTargetChange={setMergeTarget}
          sourceSearchValue={mergeSearchSource}
          targetSearchValue={mergeSearchTarget}
          onSourceSearchChange={setMergeSearchSource}
          onTargetSearchChange={setMergeSearchTarget}
          allTickets={filteredTickets}
        />
      )}

      <DeleteTicketModal
        isOpen={deleteModal.open}
        onClose={() => setDeleteModal({ open: false, ticket: null })}
        onConfirm={handleDeleteConfirm}
        ticket={deleteModal.ticket}
        emailCount={deleteModal.emails || 0}
        noteCount={deleteModal.notes || 0}
      />

      <CreateInsuranceIssueModal
        isOpen={insuranceModalOpen}
        onClose={() => {
          setInsuranceModalOpen(false);
        }}
        ticket={selectedTicketForInsurance}
        onSuccess={async () => {
          setInsuranceModalOpen(false);
          await refreshTickets();
          toast.success("Verzekeringszaak succesvol aangemaakt/gekoppeld");
        }}
      />

      <ExcelExportModal
        isOpen={excelExportModalOpen}
        onClose={() => setExcelExportModalOpen(false)}
        onConfirm={handleExcelExportWithColumns}
        availableColumns={HELPDESK_EXPORT_COLUMNS}
      />

      <UniversalPickerModal
        isOpen={bulkPhasePickerOpen}
        onClose={() => setBulkPhasePickerOpen(false)}
        onConfirm={(value) => {
          handleBulkPhaseConfirm(value as TicketInternalStatus);
        }}
        mode="helpdesk-label"
        title="Selecteer nieuwe fase voor geselecteerde tickets"
        config={{
          defaultValue: null,
          allowRemoval: false,
        }}
      />

      <SupplierSuggestionModal
        isOpen={supplierSuggestionOpen}
        onClose={() => {
          setSupplierSuggestionOpen(false);
        }}
        suppliers={suggestedSuppliers}
        label={suggestionForLabel}
        ticketIds={suggestionForTicketIds}
        onApplySupplier={applySuggestedSupplier}
        isUpdating={isUpdatingSupplier}
      />
    </>
  );
};

export const HelpdeskListPage = memo(HelpdeskListPageComponent);
